import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { ToastService } from './toast.service';
import { AuthService } from '../auth/auth.service';
import { ApiFieldErrorsService } from './api-field-errors.service';

/**
 * Whether this interceptor owns 401 handling.
 *
 * Apps that run their own auth-error interceptor (refresh-then-logout) must
 * provide `false`, otherwise both interceptors react to the same 401: this one
 * only sees mutations, logs out without clearing cookies/branding/permissions,
 * and pre-empts a refresh that would have succeeded.
 */
export const API_TOAST_OWNS_UNAUTHORIZED = new InjectionToken<boolean>(
  'API_TOAST_OWNS_UNAUTHORIZED',
  { providedIn: 'root', factory: () => true },
);

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const USER_STATUS_ENDPOINT_REGEX = /\/users\/\d+\/status(?:\?|$)/i;
const VERIFY_2FA_ENDPOINT_REGEX = /\/Auth\/verify-2fa(?:\?|$)/i;
const REFRESH_ENDPOINT_REGEX = /\/Auth\/refresh(?:\?|$)/i;

export const apiToastInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const ownsUnauthorized = inject(API_TOAST_OWNS_UNAUTHORIZED);
  const fieldErrors = inject(ApiFieldErrorsService);

  if (req.headers.get('X-Skip-Api-Toast') === 'true') {
    // The page shows its own message, but a rejected save still points at its field.
    if (!shouldHandleRequest(req.method, req.url)) {
      return next(req);
    }
    return next(req).pipe(
      tap({
        next: (event) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }
          const body = event.body as any;
          if (body && (body.success === false || body.isSuccess === false)) {
            fieldErrors.handleErrorResponse(body, event.status);
          }
        },
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            fieldErrors.handleErrorResponse(error.error, error.status);
          }
        },
      })
    );
  }

  if (!shouldHandleRequest(req.method, req.url)) {
    return next(req);
  }

  return next(req).pipe(
    tap({
      next: (event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        const body = event.body as any;
        if (body && (body.success === false || body.isSuccess === false)) {
          const errorToast = extractApiErrorToast(body);
          if (errorToast.message || errorToast.title) {
            toastService.showApiResponseError(errorToast.message, errorToast.title, errorToast.details);
          }
          fieldErrors.handleErrorResponse(body, event.status);
          return;
        }

        if (isVerify2faEndpoint(req.url)) {
          return;
        }

        if (isUserStatusEndpoint(req.url)) {
          const statusMessage = extractApiMessage(event.body);

          if (event.status >= 200 && event.status < 300 && statusMessage) {
            toastService.show(statusMessage, 'success');
            return;
          }

          if (event.status >= 200 && event.status < 300) {
            return;
          }

          if (statusMessage) {
            toastService.show(statusMessage, 'error');
          }
          return;
        }

        const message = extractApiMessage(event.body);
        if (message) {
          toastService.show(message, 'success');
        }
      },
      error: (error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return;
        }

        // Handle 401 Unauthorized - logout and show session expired message.
        // Skipped for verify-2fa, where a 401 just means the OTP code was wrong,
        // not an expired session, so it falls through to the normal error toast.
        if (error.status === 401 && !isVerify2faEndpoint(req.url)) {
          // An app with its own auth-error interceptor owns the logout and the
          // message. Returning here keeps it silent: showing the raw "Unauthorized"
          // body on top of that interceptor's session-expired toast would double up.
          if (!ownsUnauthorized) {
            return;
          }

          if (req.headers.get('X-Skip-401-Logout') !== 'true') {
            authService.logout();
            toastService.show('Your session has expired. Please login again.', 'error');
            router.navigate(['/login']);
            return;
          }
        }

        const errorToast = extractApiErrorToast(error.error);
        if (errorToast.message || errorToast.title) {
          toastService.showApiResponseError(errorToast.message, errorToast.title, errorToast.details);
        }
        fieldErrors.handleErrorResponse(error.error, error.status);
      }
    })
  );
};

function shouldHandleRequest(method: string, url: string): boolean {
  const normalizedMethod = method.toUpperCase();
  if (!MUTATION_METHODS.has(normalizedMethod)) {
    return false;
  }

  if (REFRESH_ENDPOINT_REGEX.test(url)) {
    return false;
  }

  return true;
}

function isUserStatusEndpoint(url: string): boolean {
  return USER_STATUS_ENDPOINT_REGEX.test(url);
}

function isVerify2faEndpoint(url: string): boolean {
  return VERIFY_2FA_ENDPOINT_REGEX.test(url);
}

function extractApiMessage(payload: unknown): string {
  if (typeof payload === 'string') {
    return payload.trim();
  }

  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const value = payload as {
    message?: unknown;
    error?: { message?: unknown };
    data?: { message?: unknown };
    errors?: Array<{ message?: unknown }>;
  };

  if (typeof value.error?.message === 'string' && value.error.message.trim()) {
    return value.error.message.trim();
  }

  if (typeof value.message === 'string' && value.message.trim()) {
    return value.message.trim();
  }

  if (typeof value.data?.message === 'string' && value.data.message.trim()) {
    return value.data.message.trim();
  }

  if (Array.isArray(value.errors)) {
    const firstError = value.errors.find((errorItem) => typeof errorItem?.message === 'string' && errorItem.message.trim());
    if (firstError && typeof firstError.message === 'string') {
      return firstError.message.trim();
    }
  }

  return '';
}

function extractApiErrorToast(payload: unknown): { title: string; message: string; details: (string | { field?: string; message: string })[] } {
  if (typeof payload === 'string') {
    return { title: '', message: payload.trim(), details: [] };
  }

  if (!payload || typeof payload !== 'object') {
    return { title: '', message: '', details: [] };
  }

  const value = payload as any;

  // Extract code from multiple possible locations
  const errorCode = value?.code || value?.error?.code || '';

  // Extract message from multiple possible locations
  const errorMessage = value?.error?.message || value?.message || 'An error occurred';

  // Extract details from multiple possible locations - ALWAYS check this first
  const detailsArray = value?.error?.details || value?.details;

  if (Array.isArray(detailsArray) && detailsArray.length > 0) {
    const detailMessages = collectDetailedErrorMessages(detailsArray);
    if (detailMessages && detailMessages.length > 0) {
      return {
        title: String(errorCode).trim() || '',
        message: String(errorMessage).trim() || 'An error occurred',
        details: detailMessages
      };
    }
  }

  // ASP.NET validation shape: { title, errors: { FirstName: ['First name is required'] } }.
  const errorDictionary = value?.errors ?? value?.error?.errors;
  if (errorDictionary && typeof errorDictionary === 'object' && !Array.isArray(errorDictionary)) {
    const dictionaryDetails = Object.entries(errorDictionary).flatMap(([field, messages]) =>
      collectErrorMessages(messages).map((message) => ({ field, message })),
    );
    if (dictionaryDetails.length > 0) {
      const dictionaryMessage =
        value?.error?.message || value?.message || value?.title || 'One or more validation errors occurred.';
      return {
        title: String(errorCode).trim() || '',
        message: String(dictionaryMessage).trim(),
        details: dictionaryDetails,
      };
    }
  }

  // Fallback: handle errors without detailed validation errors
  const title = String(errorCode).trim() || '';
  const innerMessages: string[] = [];

  if (!innerMessages.length) {
    innerMessages.push(...collectErrorMessages(value.error?.message));
  }

  if (!innerMessages.length && Array.isArray(value.errors)) {
    for (const item of value.errors) {
      innerMessages.push(...collectErrorMessages(item?.message));
    }
  }

  const dedupedInnerMessages = Array.from(new Set(innerMessages.map((msg) => msg.trim()).filter(Boolean)));
  const message = dedupedInnerMessages.join('\n');

  if (title && message && normalizeToastText(title) === normalizeToastText(message)) {
    return { title: '', message, details: [] };
  }

  if (title && message) {
    return { title, message, details: [] };
  }

  if (message) {
    return { title: '', message, details: [] };
  }

  if (title) {
    return { title: '', message: title, details: [] };
  }

  return { title: '', message: extractApiMessage(payload), details: [] };
}

function collectDetailedErrorMessages(value: unknown): (string | { field?: string; message: string })[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const messages: (string | { field?: string; message: string })[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') {
      messages.push(...collectErrorMessages(entry));
      continue;
    }

    const detail = entry as { field?: unknown; message?: unknown };
    const message = typeof detail.message === 'string' ? detail.message.trim() : '';
    const field = typeof detail.field === 'string' ? detail.field.trim() : undefined;

    if (message) {
      messages.push({
        field,
        message,
      });
    } else {
      messages.push(...collectErrorMessages(entry));
    }
  }

  return messages;
}

function collectErrorMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized ? [normalized] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectErrorMessages(entry));
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const objectValue = value as Record<string, unknown>;
  if (typeof objectValue['message'] === 'string') {
    return collectErrorMessages(objectValue['message']);
  }

  const messages: string[] = [];
  for (const key of Object.keys(objectValue)) {
    messages.push(...collectErrorMessages(objectValue[key]));
  }

  return messages;
}

function normalizeToastText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}
